import { Invoice } from '../../domain/entities/Invoice';
import { Payment } from '../../domain/entities/Payment';
import { InvoiceService } from '../services/InvoiceService';
import { PaymentService } from '../services/PaymentService';

/**
 * Datos requeridos para procesar un pago
 */
export interface ProcessPaymentParams {
  invoiceId: string;
  amount: number;
  paymentMethod: string;
}

/**
 * Caso de uso para procesar pagos de facturas
 */
export class ProcessPaymentUseCase {
  private paymentService: PaymentService;
  private invoiceService: InvoiceService;
  
  /**
   * Constructor del caso de uso
   * @param paymentService - Servicio de pagos a utilizar
   * @param invoiceService - Servicio de facturas a utilizar
   */
  constructor(paymentService: PaymentService, invoiceService: InvoiceService) {
    this.paymentService = paymentService;
    this.invoiceService = invoiceService;
  }
  
  /**
   * Ejecuta el caso de uso para procesar un pago
   * @param params - Parámetros para el pago
   * @returns Objeto con resultado y mensaje
   */
  async execute(params: ProcessPaymentParams): Promise<{
    success: boolean;
    payment?: Payment;
    invoice?: Invoice;
    message?: string;
    isPartialPayment?: boolean;
  }> {
    try {
      // Validaciones básicas
      if (!params.invoiceId) {
        return { success: false, message: 'ID de factura es requerido' };
      }
      
      if (params.amount <= 0) {
        return { success: false, message: 'El monto a pagar debe ser mayor a cero' };
      }
      
      if (!params.paymentMethod) {
        return { success: false, message: 'Método de pago es requerido' };
      }
      
      // Obtenemos la factura
      const invoiceList = await this.invoiceService.getAllInvoices();
      const invoice = invoiceList.find(inv => inv.id === params.invoiceId);
      
      if (!invoice) {
        return { success: false, message: 'Factura no encontrada' };
      }
      
      // Verificamos si es un pago parcial
      const isPartialPayment = params.amount !== invoice.totalAmount;
      
      // Creamos el pago
      const payment = await this.paymentService.createPayment({
        invoiceId: params.invoiceId,
        amount: params.amount,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: params.paymentMethod,
        status: 'pending',
        transactionReference: `TX${Date.now()}`
      });
      
      // Si llegamos hasta aquí, el pago fue iniciado correctamente
      return {
        success: true,
        payment,
        invoice,
        isPartialPayment
      };
      
    } catch (error) {
      console.error('Error en ProcessPaymentUseCase:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al procesar el pago'
      };
    }
  }
  
  /**
   * Actualiza el estado de un pago
   * @param paymentId - ID del pago a actualizar
   * @param status - Nuevo estado
   * @returns Objeto con resultado y mensaje
   */
  async updatePaymentStatus(paymentId: string, status: Payment['status']): Promise<{
    success: boolean;
    payment?: Payment;
    message?: string;
  }> {
    try {
      const payment = await this.paymentService.updatePaymentStatus(paymentId, status);
      
      // Si el pago fue exitoso, actualizamos la factura a pagada
      if (status === 'success') {
        const invoiceList = await this.invoiceService.getAllInvoices();
        const invoice = invoiceList.find(inv => inv.id === payment.invoiceId);
        
        if (invoice) {
          invoice.status = 'paid';
          await this.invoiceService.updateInvoice(invoice);
        }
      }
      
      return {
        success: true,
        payment
      };
      
    } catch (error) {
      console.error('Error al actualizar estado del pago:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al actualizar el estado del pago'
      };
    }
  }
} 